import axios from "axios";

import { config } from "../config";
import {
  SIGNING_CASE_STATUS,
  SIGNING_PROVIDER,
} from "../../../constants/SigningProviderConstants";

const MOCK_DELAY = process.env.NODE_ENV === "test" ? 0 : 250;
const mockSigningCases = {};

const providers = [
  {
    id: SIGNING_PROVIDER.YOUTRUST,
    name: "YouTrust",
    description: "Digitale Signatur",
    brandColor: "color2",
    active: true,
    supportedPersonTypes: ["naturliche", "juristische"],
  },
  {
    id: SIGNING_PROVIDER.NECT,
    name: "Nect Ident",
    description: "Digitale Identifizierung und Signatur",
    brandColor: "color23",
    active: true,
    supportedPersonTypes: ["naturliche", "juristische"],
  },
  {
    id: SIGNING_PROVIDER.VERIMI,
    name: "Verimi ID-Pass",
    description: "Identifizierung und Signatur über Verimi",
    brandColor: "verimiColor",
    active: true,
    supportedPersonTypes: ["naturliche", "juristische"],
  },
  {
    id: "INACTIVE_PROVIDER",
    name: "Inaktiver Anbieter",
    active: false,
    supportedPersonTypes: ["naturliche", "juristische"],
  },
];

const respond = (data) =>
  new Promise((resolve) => {
    setTimeout(() => resolve({ status: 200, data }), MOCK_DELAY);
  });

const getProviders = (personType) =>
  respond(
    providers.filter(
      (provider) =>
        provider.active && provider.supportedPersonTypes.includes(personType)
    )
  );

const selectProvider = (providerId) =>
  respond(providers.find((provider) => provider.id === providerId));

const getRepresentatives = () =>
  // MOCK: replace with the signing-provider representatives endpoint.
  respond([
    { id: "rep-1", name: "Mustafa Cavus", birthDate: "20.2.1976" },
    {
      id: "rep-2",
      name: "Markus Nikolaus Linde",
      birthDate: "20.2.1976",
    },
    { id: "rep-3", name: "Anna Katharina Becker", birthDate: "15.6.1982" },
    { id: "rep-4", name: "Felix Hoffmann", birthDate: "08.4.1970" },
    {
      id: "rep-5",
      name: "Katharina Elisabeth von Fischer-Hartmann",
      birthDate: "12.3.1984",
    },
    {
      id: "rep-6",
      name: "Christian Alexander Röhricht",
      birthDate: "20.2.1976",
    },
    {
      id: "rep-7",
      name: "Thomas Friedrich Wilhelm Müller",
      birthDate: "03.11.1975",
    },
    { id: "rep-8", name: "Sophie Margarethe Braun", birthDate: "22.9.1988" },
    { id: "rep-9", name: "Luisa Weber", birthDate: "12.1.1991" },
    { id: "rep-10", name: "Johannes Schäfer", birthDate: "30.7.1965" },
  ]);

const getSigningCase = (requestId) => {
  const signingCase = mockSigningCases[requestId];

  if (!signingCase) {
    return respond(null);
  }

  const elapsedTime = Date.now() - signingCase.createdAt;
  if (signingCase.shouldFail && elapsedTime >= 3000) {
    signingCase.status = SIGNING_CASE_STATUS.FAILED;
    signingCase.currentStep = 2;
    signingCase.errorMessage =
      "Der Signaturprozess konnte nicht abgeschlossen werden.";
  } else if (elapsedTime >= 7000) {
    signingCase.status = SIGNING_CASE_STATUS.RECEIVED;
    signingCase.currentStep = 4;
    signingCase.documents = signingCase.documents.map((document) => ({
      ...document,
      signed: true,
    }));
  } else if (elapsedTime >= 5000) {
    signingCase.status = SIGNING_CASE_STATUS.PROCESSING;
    signingCase.currentStep = 3;
  } else if (elapsedTime >= 2500) {
    signingCase.status = signingCase.isLegalPerson
      ? SIGNING_CASE_STATUS.AWAITING_SIGNER_SELECTION
      : SIGNING_CASE_STATUS.PROCESSING;
    signingCase.currentStep = 2;
  } else if (signingCase.isLegalPerson && elapsedTime >= 1000) {
    signingCase.status = SIGNING_CASE_STATUS.PENDING_VERIFICATION;
    signingCase.currentStep = 1;
  }

  return respond({ ...signingCase });
};

const createSigningCase = (requestId, signingData) => {
  const validationMessages = {};
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isLegalPerson = Boolean(signingData.companyName);

  if (!isLegalPerson && !signingData.firstName) {
    validationMessages.firstName = "Der Vorname fehlt im Vorgang.";
  }
  if (!isLegalPerson && !signingData.lastName) {
    validationMessages.lastName = "Der Nachname fehlt im Vorgang.";
  }
  if (!isLegalPerson && !emailPattern.test(signingData.email || "")) {
    validationMessages.email =
      "Bitte geben Sie eine gültige E-Mail-Adresse ein.";
  }
  if (isLegalPerson && !signingData.signers?.length) {
    validationMessages.signers =
      "Mindestens eine unterzeichnende Person ist erforderlich.";
  }
  if (
    isLegalPerson &&
    signingData.signers?.some(
      (signer) => !emailPattern.test(signer.email || "")
    )
  ) {
    validationMessages.signerEmails =
      "Für jede unterzeichnende Person ist eine gültige E-Mail-Adresse erforderlich.";
  }
  if (
    isLegalPerson &&
    signingData.signers?.some(
      (signer) => signer.isNonRepresentative && !signer.poaDocument
    )
  ) {
    validationMessages.poa =
      "Eine Vollmacht ist für die hinzugefügte Person erforderlich.";
  }
  if (!signingData.documentsAvailable) {
    validationMessages.documents =
      "Vollmacht und SEPA-Mandat konnten nicht vorbereitet werden.";
  }

  if (Object.keys(validationMessages).length) {
    return respond({ validationMessages }).then((response) => ({
      ...response,
      status: 400,
    }));
  }

  const signingCase = {
    id: `mock-signing-${requestId}`,
    requestId,
    providerId: signingData.providerId,
    providerName: signingData.providerName,
    companyName: signingData.companyName,
    hrab: signingData.hrab,
    signers: signingData.signers || [],
    isLegalPerson,
    firstName: signingData.firstName,
    lastName: signingData.lastName,
    email: signingData.email,
    status: SIGNING_CASE_STATUS.CREATED,
    currentStep: 1,
    createdAt: Date.now(),
    shouldFail:
      (signingData.email || "").toLowerCase() === "failed@example.com" ||
      signingData.signers?.some(
        (signer) => signer.email?.toLowerCase() === "failed@example.com"
      ),
    errorMessage: "",
    documents: [
      { id: "vollmacht", name: "Vollmacht.pdf", signed: false },
      { id: "sepa", name: "SEPA-Mandat.pdf", signed: false },
      ...(signingData.addressDocumentAvailable
        ? [
            {
              id: "addressDocument",
              name: "Adressnachweis.pdf",
              signed: false,
            },
          ]
        : []),
    ],
  };

  mockSigningCases[requestId] = signingCase;
  return respond({ ...signingCase });
};

const cancelSigningCase = (requestId) => {
  delete mockSigningCases[requestId];
  return respond(null);
};

const downloadPrefilledDocuments = (requestId, isPPOA = false) => {
  const endpoint = isPPOA ? `dauervollmacht/${requestId}` : `qes/${requestId}`;
  return axios.get(`${config.apiUrl}/${endpoint}/prefilled-docs`, {
    responseType: "blob",
  });
};

const downloadSigningDocument = (requestId, documentId) => {
  const signingCase = mockSigningCases[requestId];
  const document = signingCase?.documents.find(
    (item) => item.id === documentId && item.signed
  );

  if (!document) {
    return respond({
      message: "Das signierte Dokument ist nicht verfügbar.",
    }).then((response) => ({ ...response, status: 404 }));
  }

  return respond({
    fileName: document.name,
    content: `Mock signed document: ${document.name}`,
  });
};

const startVerification = (
  requestId,
  providerId,
  additionalFields,
  isPPOA = false
) => {
  const endpoint = isPPOA
    ? `dauervollmacht/${requestId}/qes`
    : `qes/${requestId}`;
  const url = `${config.apiUrl}/${endpoint}`;

  const payload =
    providerId === "VERIMI"
      ? {
          providerId,
          ...additionalFields,
        }
      : {
          providerId,
          additionalFields,
        };

  return axios.post(url, payload);
};

const submitSigners = (requestId, signers, isPPOA = false) => {
  const endpoint = isPPOA
    ? `dauervollmacht/${requestId}/signers`
    : `qes/${requestId}/signers`;
  return axios.put(`${config.apiUrl}/${endpoint}`, { signers });
};

export const signingProviderService = {
  getProviders,
  selectProvider,
  getRepresentatives,
  getSigningCase,
  createSigningCase,
  cancelSigningCase,
  downloadPrefilledDocuments,
  downloadSigningDocument,
  startVerification,
  submitSigners,
};
